"use client";

import { useState } from "react";
import { Bell, CheckCircle, Mail, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

interface StockNotifyFormProps {
  productSlug: string;
}

export function StockNotifyForm({ productSlug }: StockNotifyFormProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      await api.post(`/api/catalog/products/${productSlug}/stock-notify`, { email });
      setStatus("success");
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Bir hata oluştu. Lütfen tekrar deneyin.");
    }
  }

  if (status === "success") {
    return (
      <div className="flex items-center gap-3 rounded-[var(--sf-radius)] border border-[var(--sf-success-border)] bg-[var(--sf-success-bg)] px-4 py-3 text-sm text-[var(--sf-success-fg)]">
        <CheckCircle className="h-5 w-5 shrink-0" />
        <span>Ürün stoğa girdiğinde sizi e-posta ile bilgilendireceğiz.</span>
      </div>
    );
  }

  return (
    <div className="rounded-[var(--sf-radius)] border border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/5 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--sf-primary)]/10">
          <AlertTriangle className="h-5 w-5 text-[var(--sf-primary)]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Bu ürün şu an stokta yok
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            E-posta adresinizi bırakın, stoka girdiğinde haber verelim.
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-posta adresiniz"
            disabled={status === "loading"}
            className="w-full h-10 pl-10 pr-3 rounded-[var(--sf-radius)] border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sf-primary)]/20 focus:border-[var(--sf-primary)] disabled:opacity-60 transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className="shrink-0 h-10 px-4 rounded-[var(--sf-radius)] text-sm font-semibold text-white bg-[var(--sf-primary)] hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {status === "loading" ? (
            <span className="inline-flex items-center gap-1.5">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Gönderiliyor
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Bell className="h-4 w-4" />
              Haber Ver
            </span>
          )}
        </button>
      </form>
      {status === "error" && (
        <p className="text-xs text-[var(--sf-danger-fg)]">{errorMsg}</p>
      )}
    </div>
  );
}
